import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { TypographyComponent } from '@purolator/ui-kit-angular';
import { filter, map } from 'rxjs';
import { TranslatePipe } from '../../core/i18n';

interface BrowserUrlSnapshot {
  href: string;
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Temporary harness for validating in-app routing, redirects, anchors, and query params.
 */
@Component({
  selector: 'app-routing-test',
  imports: [RouterLink, TypographyComponent, TranslatePipe],
  templateUrl: './routing-test.html',
  styleUrl: './routing-test.scss',
})
export class RoutingTest {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly browserUrl = signal<BrowserUrlSnapshot>(this.readBrowserUrl());
  protected readonly routerUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );
  protected readonly queryParams = toSignal(this.route.queryParamMap, {
    initialValue: this.route.snapshot.queryParamMap,
  });
  protected readonly routeParams = toSignal(this.route.paramMap, {
    initialValue: this.route.snapshot.paramMap,
  });
  protected readonly fragment = toSignal(this.route.fragment, {
    initialValue: this.route.snapshot.fragment,
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.browserUrl.set(this.readBrowserUrl()));
  }

  /**
   * Navigates within the app using Angular router query params and a fragment.
   */
  navigateWithQueryAndFragment(): void {
    void this.router.navigate(['/routing-test'], {
      queryParams: { tab: 'beta', source: 'harness' },
      fragment: 'section-beta',
    });
  }

  private readBrowserUrl(): BrowserUrlSnapshot {
    if (typeof window === 'undefined') {
      return { href: '', pathname: '', search: '', hash: '' };
    }

    return {
      href: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    };
  }
}
